import React from 'react';

import { Icon } from '@chakra-ui/react';
import {
  MdPerson,
  MdHome,
  MdArticle,
  MdAdd,
  MdSchool,
  MdPeople,
  MdAccountCircle,
  MdSettings,
  MdMonitor,
  MdSupportAgent,
  MdLocationOn,
  MdNotifications,
  MdEmail,
  MdPublic,
  MdAttachMoney,
  MdBarChart,
} from 'react-icons/md';

// Admin Imports
import MainDashboard from 'views/admin/default';
import BloggerDashboard from 'views/admin/blogger';
import CenterAdminDashboard from 'views/admin/centerAdmin';
import NFTMarketplace from 'views/admin/marketplace';
import Profile from 'views/admin/profile';
import CreateBlog from 'views/admin/blogger/createBlog';
import TuitionManagement from 'views/admin/tuition';
import ViewTuition from 'views/admin/tuition/viewTuition';
import ViewPayment from 'views/admin/tuition/viewPayment';
import StudentsManagement from 'views/admin/students';
import ViewStudent from 'views/admin/students/viewStudent';
import TutorsManagement from 'views/admin/tutors';
import ViewTutor from 'views/admin/tutors/viewTutor';
import UsersManagement from 'views/admin/users';
import ViewUser from 'views/admin/users/viewUser';
import SystemManagement from 'views/admin/system';
import Monitoring from 'views/admin/monitoring';
import ViewRecipient from 'views/admin/monitoring/viewRecipient';
import ViewReport from 'views/admin/monitoring/viewReport';
import Support from 'views/admin/support';
import SupportConversation from 'views/admin/support/conversation';
import CentersManagement from 'views/admin/centers';
import CenterForm from 'views/admin/centers/CenterForm';
import RegionsManagement from 'views/admin/regions';
import AmountsManagement from 'views/admin/amounts';
import Notifications from 'views/admin/notifications';
import Mails from 'views/admin/mails';
import IReferalsOverview from 'views/admin/ireferals';
import IReferalsReports from 'views/admin/ireferals/reports';
import IReferalsProcessed from 'views/admin/ireferals/processed';
import ReferalsUnprocessed from 'views/admin/ireferals/unprocessed';
import ReferalsFailed from 'views/admin/ireferals/failed';
import StudentReports from 'views/admin/studentReports';
import StudentClassListReport from 'views/admin/studentReports/classList';
import TuitionPaymentsReport from 'views/admin/studentReports/tuitionPayments';

// Auth Imports
import SignIn from 'views/auth/signIn';
import AcceptInvite from 'views/auth/acceptInvite';

// Constants
import { USER_ROLES } from 'constants/roles';

const routes = [
  // Auth Routes
  {
    name: 'Sign In',
    layout: '/auth',
    path: '/sign-in/default',
    component: <SignIn />,
    hidden: true, // Don't show in sidebar
  },
  {
    name: 'Sign In',
    layout: '/auth',
    path: '/sign-in',
    component: <SignIn />,
    hidden: true, // Don't show in sidebar
  },
  {
    name: 'Accept Invite',
    layout: '/auth',
    path: '/accept-invite',
    component: <AcceptInvite />,
    hidden: true,
  },

  // Dashboards
  {
    name: 'Main Dashboard',
    layout: '/admin',
    path: '/default',
    icon: <Icon as={MdHome} width="20px" height="20px" color="inherit" />,
    component: <MainDashboard />,
    allowedRoles: [USER_ROLES.ADMIN],
  },
  {
    name: 'Blogger Dashboard',
    layout: '/admin',
    path: '/blogger',
    icon: <Icon as={MdHome} width="20px" height="20px" color="inherit" />,
    component: <BloggerDashboard />,
    allowedRoles: [USER_ROLES.BLOGGER],
  },
  {
    name: 'Center Admin Dashboard',
    layout: '/admin',
    path: '/center-admin',
    icon: <Icon as={MdHome} width="20px" height="20px" color="inherit" />,
    component: <CenterAdminDashboard />,
    allowedRoles: [USER_ROLES.CENTER_ADMIN],
  },

  // Student and tuition workflows
  {
    name: 'Student & Tuition',
    category: true,
    icon: <Icon as={MdSchool} width="20px" height="20px" color="inherit" />,
    allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.CENTER_ADMIN],
    items: [
      {
        name: 'Students',
        layout: '/admin',
        icon: <Icon as={MdPeople} width="20px" height="20px" color="inherit" />,
        path: '/students',
        component: <StudentsManagement />,
        allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.CENTER_ADMIN],
      },
      {
        name: 'Tuition',
        layout: '/admin',
        icon: <Icon as={MdAttachMoney} width="20px" height="20px" color="inherit" />,
        path: '/tuition',
        component: <TuitionManagement />,
        allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.CENTER_ADMIN],
      },
      {
        name: 'Reports',
        layout: '/admin',
        icon: <Icon as={MdBarChart} width="20px" height="20px" color="inherit" />,
        path: '/student-reports',
        component: <StudentReports />,
        allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.CENTER_ADMIN],
      },
      {
        name: 'Student Class List Report',
        layout: '/admin',
        path: '/student-reports/class-list',
        component: <StudentClassListReport />,
        allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.CENTER_ADMIN],
        hidden: true,
      },
      {
        name: 'Tuition Payments Report',
        layout: '/admin',
        path: '/student-reports/tuition-payments',
        component: <TuitionPaymentsReport />,
        allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.CENTER_ADMIN],
        hidden: true,
      },
      {
        name: 'View Student',
        layout: '/admin',
        path: '/student/:id',
        component: <ViewStudent />,
        allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.CENTER_ADMIN],
        hidden: true,
      },
      {
        name: 'View Tuition',
        layout: '/admin',
        path: '/tuition/:id',
        component: <ViewTuition />,
        allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.CENTER_ADMIN],
        hidden: true,
      },
      {
        name: 'View Payment',
        layout: '/admin',
        path: '/payment/:id',
        component: <ViewPayment />,
        allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.CENTER_ADMIN],
        hidden: true,
      },
    ],
  },

  // Academic setup
  {
    name: 'Academic Setup',
    category: true,
    icon: <Icon as={MdLocationOn} width="20px" height="20px" color="inherit" />,
    allowedRoles: [USER_ROLES.ADMIN],
    items: [
      {
        name: 'Centers',
        layout: '/admin',
        icon: <Icon as={MdLocationOn} width="20px" height="20px" color="inherit" />,
        path: '/centers',
        component: <CentersManagement />,
        allowedRoles: [USER_ROLES.ADMIN],
      },
      {
        name: 'Regions',
        layout: '/admin',
        icon: <Icon as={MdPublic} width="20px" height="20px" color="inherit" />,
        path: '/regions',
        component: <RegionsManagement />,
        allowedRoles: [USER_ROLES.ADMIN],
      },
      {
        name: 'Amounts',
        layout: '/admin',
        icon: <Icon as={MdAttachMoney} width="20px" height="20px" color="inherit" />,
        path: '/amounts',
        component: <AmountsManagement />,
        allowedRoles: [USER_ROLES.ADMIN],
      },
      {
        name: 'Tutors',
        layout: '/admin',
        icon: <Icon as={MdPerson} width="20px" height="20px" color="inherit" />,
        path: '/tutors',
        component: <TutorsManagement />,
        allowedRoles: [USER_ROLES.ADMIN],
      },
      {
        name: 'New Center',
        layout: '/admin',
        path: '/centers/new',
        component: <CenterForm />,
        allowedRoles: [USER_ROLES.ADMIN],
        hidden: true,
      },
      {
        name: 'Edit Center',
        layout: '/admin',
        path: '/centers/:id/edit',
        component: <CenterForm />,
        allowedRoles: [USER_ROLES.ADMIN],
        hidden: true,
      },
      {
        name: 'View Tutor',
        layout: '/admin',
        path: '/tutor/:id',
        component: <ViewTutor />,
        allowedRoles: [USER_ROLES.ADMIN],
        hidden: true,
      },
    ],
  },

  // Content
  {
    name: 'Content',
    category: true,
    icon: <Icon as={MdArticle} width="20px" height="20px" color="inherit" />,
    allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.BLOGGER],
    items: [
      {
        name: 'Blogs',
        layout: '/admin',
        icon: <Icon as={MdArticle} width="20px" height="20px" color="inherit" />,
        path: '/blogs',
        component: <NFTMarketplace />,
        allowedRoles: [USER_ROLES.ADMIN],
      },
      {
        name: 'Manage Blogs',
        layout: '/admin',
        icon: <Icon as={MdArticle} width="20px" height="20px" color="inherit" />,
        path: '/manage-blogs',
        component: <NFTMarketplace />,
        allowedRoles: [USER_ROLES.BLOGGER],
      },
      {
        name: 'Create New Blog',
        layout: '/admin',
        path: '/create-blog',
        icon: <Icon as={MdAdd} width="20px" height="20px" color="inherit" />,
        component: <CreateBlog />,
        allowedRoles: [USER_ROLES.BLOGGER],
      },
      {
        name: 'Edit Blog',
        layout: '/admin',
        path: '/edit-blog/:id',
        icon: <Icon as={MdAdd} width="20px" height="20px" color="inherit" />,
        component: <CreateBlog />,
        allowedRoles: [USER_ROLES.BLOGGER],
        hidden: true,
      },
    ],
  },

  // Referals (Admin only)
  {
    name: 'Referals',
    category: true,
    icon: <Icon as={MdPeople} width="20px" height="20px" color="inherit" />,
    allowedRoles: [USER_ROLES.ADMIN],
    items: [
      {
        name: 'Overview',
        layout: '/admin',
        path: '/referals',
        component: <IReferalsOverview />,
        allowedRoles: [USER_ROLES.ADMIN],
      },
      {
        name: 'Reports',
        layout: '/admin',
        path: '/referals/reports',
        component: <IReferalsReports />,
        allowedRoles: [USER_ROLES.ADMIN],
      },
      {
        name: 'Processed',
        layout: '/admin',
        path: '/referals/processed',
        component: <IReferalsProcessed />,
        allowedRoles: [USER_ROLES.ADMIN],
      },
      {
        name: 'Unprocessed',
        layout: '/admin',
        path: '/referals/unprocessed',
        component: <ReferalsUnprocessed />,
        allowedRoles: [USER_ROLES.ADMIN],
      },
      {
        name: 'Failed',
        layout: '/admin',
        path: '/referals/failed',
        component: <ReferalsFailed />,
        allowedRoles: [USER_ROLES.ADMIN],
      },
    ],
  },

  // Communications
  {
    name: 'Communications',
    category: true,
    icon: <Icon as={MdSupportAgent} width="20px" height="20px" color="inherit" />,
    allowedRoles: [USER_ROLES.ADMIN],
    items: [
      {
        name: 'Support',
        layout: '/admin',
        icon: <Icon as={MdSupportAgent} width="20px" height="20px" color="inherit" />,
        path: '/support',
        component: <Support />,
        allowedRoles: [USER_ROLES.ADMIN],
      },
      {
        name: 'Notifications',
        layout: '/admin',
        icon: <Icon as={MdNotifications} width="20px" height="20px" color="inherit" />,
        path: '/notifications',
        component: <Notifications />,
        allowedRoles: [USER_ROLES.ADMIN],
      },
      {
        name: 'Mails',
        layout: '/admin',
        icon: <Icon as={MdEmail} width="20px" height="20px" color="inherit" />,
        path: '/mails',
        component: <Mails />,
        allowedRoles: [USER_ROLES.ADMIN],
      },
      {
        name: 'View Conversation',
        layout: '/admin',
        path: '/support/conversation/:id',
        component: <SupportConversation />,
        allowedRoles: [USER_ROLES.ADMIN],
        hidden: true,
      },
    ],
  },

  // Administration
  {
    name: 'Administration',
    category: true,
    icon: <Icon as={MdSettings} width="20px" height="20px" color="inherit" />,
    allowedRoles: [USER_ROLES.ADMIN],
    items: [
      {
        name: 'Users',
        layout: '/admin',
        icon: <Icon as={MdAccountCircle} width="20px" height="20px" color="inherit" />,
        path: '/users',
        component: <UsersManagement />,
        allowedRoles: [USER_ROLES.ADMIN],
      },
      {
        name: 'System',
        layout: '/admin',
        icon: <Icon as={MdSettings} width="20px" height="20px" color="inherit" />,
        path: '/system',
        component: <SystemManagement />,
        allowedRoles: [USER_ROLES.ADMIN],
      },
      {
        name: 'Monitoring',
        layout: '/admin',
        icon: <Icon as={MdMonitor} width="20px" height="20px" color="inherit" />,
        path: '/monitoring',
        component: <Monitoring />,
        allowedRoles: [USER_ROLES.ADMIN],
      },
      {
        name: 'Reports',
        layout: '/admin',
        icon: <Icon as={MdArticle} width="20px" height="20px" color="inherit" />,
        path: '/reports',
        component: <ViewReport />,
        allowedRoles: [USER_ROLES.ADMIN],
      },
      {
        name: 'View User',
        layout: '/admin',
        path: '/user/:id',
        component: <ViewUser />,
        allowedRoles: [USER_ROLES.ADMIN],
        hidden: true,
      },
      {
        name: 'View Recipient',
        layout: '/admin',
        path: '/recipient/:id',
        component: <ViewRecipient />,
        allowedRoles: [USER_ROLES.ADMIN],
        hidden: true,
      },
      {
        name: 'View Report',
        layout: '/admin',
        path: '/report/:date',
        component: <ViewReport />,
        allowedRoles: [USER_ROLES.ADMIN],
        hidden: true,
      },
    ],
  },

  // Profile (All authenticated users)
  {
    name: 'Profile',
    layout: '/admin',
    path: '/profile',
    icon: <Icon as={MdPerson} width="20px" height="20px" color="inherit" />,
    component: <Profile />,
    // No allowedRoles means all authenticated users can access
  },
];

export default routes;
